import { useState, useEffect, useMemo } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

interface CampaignRow {
  campaignId: string | null;
  campaignName: string | null;
  spend: number;
  clicks: number;
  impressions: number;
  revenue: number;
  roas: number;
  calls: number;
  rpc: number;
}

type SortKey = 'campaignName' | 'spend' | 'clicks' | 'impressions' | 'revenue' | 'roas' | 'calls' | 'rpc';

function SortIcon({ dir }: { dir: 'asc' | 'desc' | null }) {
  if (!dir) return <span className="ml-1 text-slate-600">↕</span>;
  return <span className="ml-1 text-blue-400">{dir === 'asc' ? '↑' : '↓'}</span>;
}

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {Array.from({ length: 7 }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-slate-800 rounded w-20" />
        </td>
      ))}
    </tr>
  );
}

export function CampaignTable() {
  const [rows, setRows] = useState<CampaignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    const today = new Date().toISOString().slice(0, 10);
    trpc.roas.byCampaign.query({ tenantId: user.tenantId, startDate: today, endDate: today })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((data: any[]) => setRows(data))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir('desc'); }
  }

  const filtered = useMemo(() => {
    return rows
      .filter(c => (c.campaignName ?? '').toLowerCase().includes(filter.toLowerCase()))
      .sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        const cmp = typeof av === 'string'
          ? (av ?? '').localeCompare(bv as string ?? '')
          : ((av as number) ?? 0) - ((bv as number) ?? 0);
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [rows, filter, sortKey, sortDir]);

  function th(label: string, key: SortKey) {
    return (
      <th
        className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-300 select-none whitespace-nowrap"
        onClick={() => toggleSort(key)}
      >
        {label}<SortIcon dir={sortKey === key ? sortDir : null} />
      </th>
    );
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Campaigns</h3>
        <input
          type="text"
          placeholder="Filter campaigns…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-300 placeholder-slate-600 focus:outline-none focus:border-blue-500 w-48"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-950/50">
            <tr>
              {th('Campaign', 'campaignName')}
              {th('Spend', 'spend')}
              {th('Clicks', 'clicks')}
              {th('Impressions', 'impressions')}
              {th('Revenue', 'revenue')}
              {th('ROAS', 'roas')}
              {th('Calls', 'calls')}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
            ) : error ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-sm">Failed to load campaign data.</td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-sm">
                  {rows.length === 0 ? 'No campaign data available yet.' : 'No campaigns match your filter.'}
                </td>
              </tr>
            ) : (
              filtered.map((c, i) => (
                <tr key={c.campaignId ?? i} className="hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3 text-sm text-slate-200 font-medium whitespace-nowrap">
                    {c.campaignName ?? 'Unknown Campaign'}
                  </td>
                  <td className="px-4 py-3 text-sm text-rose-400">${c.spend.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-3 text-sm text-slate-300">{c.clicks.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{c.impressions.toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm text-emerald-400">
                    {c.revenue > 0 ? `$${c.revenue.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {c.roas > 0 ? (
                      <span className={c.roas >= 2.5 ? 'text-emerald-400' : c.roas >= 1.5 ? 'text-amber-400' : 'text-rose-400'}>
                        {c.roas.toFixed(2)}x
                      </span>
                    ) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-300">{c.calls > 0 ? c.calls : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
